import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import {
  PermissionEnum,
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonPseudo,
  Popup,
  SidePanelLoader,
  PermissionDenied,
} from '@ui/src';
import { useAppSelector } from '@store';
import { RiskScoreClientTypeEnum } from '../../types';
import useRiskScore from '../../helpers/use-risk-score';
import { ProductsFormTypes } from './types';

import ROUTES from '../../../routes';

interface FormValues {
  name: string;
  riskScoreRangeId: string;
  riskScore: string;
  clientType: string;
}

const ProductsForm = ({
  panelIsOpen,
  setPanelIsOpen,
  clientType,
  getList,
  updateProduct,
  createProduct,
  isSuccess,
  item,
  isLoading,
  requestInProgress,
  updateInProgress,
  updateSuccess,
  isFetching,
}: ProductsFormTypes) => {
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [defaultValues, setDefaultValues] = useState({
    name: '',
    riskScore: '',
    riskScoreRangeId: '',
  });

  const {
    amlDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const { productId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      clientType,
      name: '',
      riskScore: '',
      riskScoreRangeId: '',
    },
  });

  const riskScore = watch('riskScoreRangeId');
  const name = watch('name');
  const riskScoreNumber = watch('riskScore');

  const isDirty = !isEqual(defaultValues, {
    name,
    riskScore: riskScoreNumber,
    riskScoreRangeId: riskScore,
  });

  const buttonVisible = productId
    ? activePermissions?.includes(PermissionEnum.SETTINGS_PRODUCTS_EDIT_ACTION)
    : activePermissions?.includes(
        PermissionEnum.SETTINGS_PRODUCTS_CREATE_ACTION,
      );

  const { onFormSubmit, form, globalError } = useRiskScore({
    handleSubmit,
    itemId: productId,
    setError,
    getList: (query: string) => getList(query),
    update: updateProduct,
    create: createProduct,
    setValue,
    errors,
    control,
    withClientType: true,
    nameTitle: 'Name',
    disabled: !buttonVisible,
  });

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(
        `${
          clientType === RiskScoreClientTypeEnum.INDIVIDUAL
            ? ROUTES.INDIVIDUAL_CLIENTS_RISK_PRODUCTS
            : ROUTES.CORPORATE_CLIENTS_RISK_PRODUCTS
        }${search || ''}`,
      );
    }, 100);
  }, []);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (updateSuccess) {
      setDefaultValues({
        name,
        riskScore: riskScoreNumber,
        riskScoreRangeId: riskScoreNumber,
      });

      handleClose();
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (item) {
      reset({
        name: item?.name,
        riskScore: item?.riskScore.toString(),
        riskScoreRangeId: item.riskScoreRangeId,
      });

      setDefaultValues({
        name: item?.name,
        riskScore: item?.riskScore.toString(),
        riskScoreRangeId: item.riskScoreRangeId,
      });
    }
  }, [reset, item]);

  let content = form;
  let buttonText;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else if (
    !isLoading &&
    !productId &&
    !activePermissions?.includes(PermissionEnum.SETTINGS_PRODUCTS_CREATE_ACTION)
  ) {
    content = <PermissionDenied />;
  }

  if (productId) {
    buttonText = 'Save product';
  } else {
    buttonText = 'Add product';
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={`${productId ? 'Edit' : 'Add'} product`}
        errorSidepanel={globalError}
        sidepanelExpandedKey="product-panel"
        footer={
          <>
            {buttonVisible && (
              <ButtonPrimary
                text={
                  requestInProgress || updateInProgress
                    ? 'Loading...'
                    : buttonText
                }
                onClick={onFormSubmit}
                disabled={!riskScore || !name}
                bold
              />
            )}

            <ButtonPseudo
              text="Cancel"
              className="button-white"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default ProductsForm;
